// Web Animations primitives for the changelog scenes (ported from the reference changelog build).
// Every animation uses `fill: "both"`, so an element sits on its first keyframe from the moment the
// scene starts, even while its delay is still pending.

const SVG_NS = "http://www.w3.org/2000/svg";

export const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";
export const EASE_IN_OUT = "cubic-bezier(0.65, 0, 0.35, 1)";
const EASE_BACK = "cubic-bezier(0.34, 1.56, 0.64, 1)";

/** Matches a text node that is only a number: `+12.4%`, `US$ 1,204`, `-3.2k`, `88 USD`. */
export const NUM = /^[+\-]?\s*(US)?\$?\s*[+\-]?\d[\d.,]*\s*(%|k|USD)?$/;

export interface TimingOptions {
  delay?: number;
  duration?: number;
  easing?: string;
}

export const stagger = (index: number, step: number, base = 0) => base + index * step;

export function byId(root: Element, prefix: string, name: string) {
  return root.querySelector<SVGElement>(`[id="${CSS.escape(`${prefix}__${name}`)}"]`);
}

export function byIds(root: Element, prefix: string, names: string[]) {
  return names.map((name) => byId(root, prefix, name)).filter((el): el is SVGElement => !!el);
}

/** Direct children of a layer, minus `<defs>`. */
export function children(el: Element | null) {
  return el ? Array.from(el.children).filter((child): child is SVGElement => child instanceof SVGElement && child.tagName !== "defs") : [];
}

/**
 * Wraps an element in a `<g data-motion>` so transforms never fight its own `transform` attribute.
 * A preceding `<foreignObject>` (Figma's backdrop-blur layer) moves into the group with it.
 */
function motionGroup(el: SVGElement): SVGElement {
  if (el instanceof SVGSVGElement) return el;
  const parent = el.parentNode;
  if (!parent) return el;
  if (parent instanceof SVGElement && parent.getAttribute("data-motion") === "") return parent;

  const group = document.createElementNS(SVG_NS, "g");
  group.setAttribute("data-motion", "");
  const previous = el.previousElementSibling;
  parent.insertBefore(group, el);
  if (previous?.tagName === "foreignObject") group.appendChild(previous);
  group.appendChild(el);
  return group;
}

function originGroup(el: SVGElement, origin: string) {
  const group = motionGroup(el);
  group.style.transformBox = "fill-box";
  group.style.transformOrigin = origin;
  return group;
}

function play(el: Element, keyframes: Keyframe[], { delay = 0, duration = 700, easing = EASE_OUT }: TimingOptions) {
  return el.animate(keyframes, { delay, duration, easing, fill: "both" });
}

/**
 * Fades an element in to its own opacity (Figma layers often carry `opacity="0.4"`), so the end
 * frame matches the static artwork. Runs after `playChangelogScene` cancelled prior animations,
 * so the computed value is the element's base opacity.
 */
export function fade(el: SVGElement | null, options: TimingOptions = {}) {
  if (!el) return;
  const opacity = Number.parseFloat(getComputedStyle(el).opacity);
  play(el, [{ opacity: 0 }, { opacity: Number.isFinite(opacity) ? opacity : 1 }], options);
}

export function rise(el: SVGElement | null, options: TimingOptions & { distance?: number } = {}) {
  if (!el) return;
  const distance = options.distance ?? 14;
  play(
    originGroup(el, "50% 50%"),
    [
      { opacity: 0, transform: `translateY(${distance}px)` },
      { opacity: 1, transform: "translateY(0)" },
    ],
    options,
  );
}

export function slideX(el: SVGElement | null, options: TimingOptions & { dx?: number } = {}) {
  if (!el) return;
  const dx = options.dx ?? -16;
  play(
    motionGroup(el),
    [
      { opacity: 0, transform: `translateX(${dx}px)` },
      { opacity: 1, transform: "translateX(0)" },
    ],
    options,
  );
}

export function grow(el: SVGElement | null, options: TimingOptions & { axis?: "x" | "y"; origin?: string } = {}) {
  if (!el) return;
  const { axis = "y", origin, ...timing } = options;
  play(
    originGroup(el, origin ?? (axis === "y" ? "50% 100%" : "0% 50%")),
    [{ transform: axis === "y" ? "scaleY(0)" : "scaleX(0)" }, { transform: "scale(1)" }],
    { easing: EASE_OUT, ...timing },
  );
}

export function pop(el: SVGElement | null, options: TimingOptions & { origin?: string } = {}) {
  if (!el) return;
  const { origin, ...timing } = options;
  play(
    originGroup(el, origin ?? "50% 50%"),
    [
      { opacity: 0, transform: "scale(0.4)" },
      { opacity: 1, transform: "scale(1)" },
    ],
    { duration: 520, easing: EASE_BACK, ...timing },
  );
}

/** Stroke draw-on via dash offset. */
export function draw(el: SVGElement | null, options: TimingOptions = {}) {
  if (!(el instanceof SVGGeometryElement)) return;
  const length = el.getTotalLength();
  el.style.strokeDasharray = `${length}`;
  play(el, [{ strokeDashoffset: length }, { strokeDashoffset: 0 }], { duration: 1600, easing: EASE_IN_OUT, ...options });
}

/** Left-to-right clip reveal. */
export function wipe(el: SVGElement | null, options: TimingOptions = {}) {
  if (!el) return;
  play(motionGroup(el), [{ clipPath: "inset(0 100% 0 0)" }, { clipPath: "inset(0 0% 0 0)" }], {
    duration: 1600,
    easing: EASE_IN_OUT,
    ...options,
  });
}

/** Staggered slide-in for list rows. */
export function rows(els: SVGElement[], base = 200, step = 55) {
  els.forEach((el, index) => slideX(el, { delay: stagger(index, step, base), duration: 600, dx: -10 }));
}

// Text effects run on requestAnimationFrame. Each run bumps `svg.dataset.run`; a loop that sees a
// newer run id stops, so a replay never races the previous one.

function finalText(el: SVGElement) {
  return (el.dataset.final ??= el.textContent ?? "");
}

function runToken(el: SVGElement) {
  const svg: SVGElement = el.ownerSVGElement ?? el;
  return { svg, id: svg.dataset.run };
}

const isStale = (token: ReturnType<typeof runToken>) => token.svg.dataset.run !== token.id;

/** Counts the first number in a text node up from 0, keeping its separators, decimals and affixes. */
export function countUp(el: SVGTextElement, options: { delay?: number; duration?: number } = {}) {
  const node: SVGTextContentElement = el.querySelector<SVGTSpanElement>("tspan") ?? el;
  const final = finalText(node);
  const token = runToken(node);
  const match = final.match(/\d[\d.,]*/);
  if (!match) return;

  const raw = match[0];
  const separators = raw.match(/[.,]/g) ?? [];
  const lastSeparator = Math.max(raw.lastIndexOf("."), raw.lastIndexOf(","));
  const tail = lastSeparator >= 0 ? raw.slice(lastSeparator + 1) : "";
  // A trailing group of exactly three digits reads as thousands, anything else as decimals.
  const hasDecimals = lastSeparator >= 0 && tail.length !== 3;
  const decimalSeparator = hasDecimals ? raw[lastSeparator] : "";
  const groupSeparator = separators.find((sep) => sep !== decimalSeparator) ?? (hasDecimals ? "" : separators[0] ?? "");
  const decimals = hasDecimals ? tail.length : 0;
  const target = parseFloat(
    raw
      .split("")
      .filter((char) => /\d/.test(char) || (hasDecimals && char === decimalSeparator))
      .join("")
      .replace(decimalSeparator || "\0", "."),
  );

  const format = (value: number) => {
    const [whole, fraction] = value.toFixed(decimals).split(".");
    const grouped = groupSeparator ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator) : whole;
    return final.replace(raw, fraction ? `${grouped}${decimalSeparator}${fraction}` : grouped);
  };

  const { delay = 0, duration = 1400 } = options;
  node.textContent = format(0);
  const start = performance.now() + delay;
  const tick = (now: number) => {
    if (isStale(token)) return;
    const progress = Math.min(1, Math.max(0, (now - start) / duration));
    const eased = 1 - Math.pow(1 - progress, 3);
    node.textContent = progress >= 1 ? final : format(target * eased);
    if (progress < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** Types a text node out character by character across its tspans. */
export function typewriter(el: SVGTextElement, options: { delay?: number; speed?: number } = {}) {
  const tspans = Array.from(el.querySelectorAll<SVGTSpanElement>("tspan"));
  const nodes: SVGTextContentElement[] = tspans.length ? tspans : [el];
  const finals = nodes.map(finalText);
  const token = runToken(el);
  nodes.forEach((node) => (node.textContent = ""));

  const speed = options.speed ?? 28;
  const total = finals.join("").length;
  const start = performance.now() + (options.delay ?? 0);
  let shown = 0;
  const tick = (now: number) => {
    if (isStale(token)) return;
    if (now >= start) {
      let budget = (shown = Math.min(total, Math.floor((now - start) / speed)));
      nodes.forEach((node, index) => {
        const take = Math.min(budget, finals[index].length);
        node.textContent = finals[index].slice(0, take);
        budget -= take;
      });
    }
    if (shown < total) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** `<text>` nodes whose content matches `pattern` and whose font-size is at least `minFontSize`. */
export function textsMatching(root: Element, pattern: RegExp, minFontSize = 0) {
  return Array.from(root.querySelectorAll<SVGTextElement>("text")).filter(
    (text) => pattern.test((text.textContent ?? "").trim()) && Number(text.getAttribute("font-size") ?? 0) >= minFontSize,
  );
}

/** Filled rects/paths shaped like vertical bars (narrow, taller than wide). */
export function bars(root: Element | null, maxWidth = 40) {
  if (!root) return [];
  return Array.from(root.querySelectorAll<SVGGraphicsElement>("rect, path")).filter((el) => {
    if (el.getAttribute("stroke") && !el.getAttribute("fill")) return false;
    const box = el.getBBox();
    return box.width > 2 && box.width <= maxWidth && box.height > box.width * 1.2;
  });
}

/** Unfilled stroked paths longer than `minLength` (chart lines). */
export function strokes(root: Element, minLength = 120) {
  return Array.from(root.querySelectorAll<SVGPathElement>("path")).filter((el) => {
    const stroke = el.getAttribute("stroke");
    const fill = el.getAttribute("fill");
    return !!stroke && stroke !== "none" && (!fill || fill === "none") && el.getTotalLength() > minLength;
  });
}
